import {Button} from '@mui/material'
import {css, styled} from '@mui/material/styles'
import Checkbox from '@ui/checkbox/FormikCheckbox'
import ErrorBox from '@ui/error/ErrorBox'
import LoadingBoundary from '@ui/loader'
import FormikTextField from '@ui/mui-text-field/FormikTextField'
import PasswordFormikTextField from '@ui/mui-text-field/PasswordFormikTextField'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {Form, Formik} from 'formik'
import queryString from 'query-string'
import React, {useCallback, useEffect, useState} from 'react'
import {NavLink, useLocation, useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {errorText} from '~/src/components/auth/constants/helper-text'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {SESSION_EXPIRED_KEY, setSessionLoginTime} from '~/src/context/SessionTimeoutContext'
import type {FirstSigninResponse, IUserBase, SigninResponse, MSPAuthResponse} from '~/src/models'
import {
  extractUserFromToken,
  createAuthResponseFromToken,
  isTokenExpired,
} from '~/src/utils/jwt-utils'
import {BASE_ROUTES} from '~/src/routes/constants'
import {useSigninMutation} from '~/src/store/apis/auth-api'
import {useLazyGetCurrentUserQuery} from '~/src/store/apis/user-api'
import {useLazyGetGaAgencyNamesAndIdsQuery} from '~/src/store/apis/member-access-api'
import {roleApi} from '~/src/services'
import {
  updateLoggedInAgencyId,
  updateLoggedInCompanyId,
  updateUserSignIn,
  updateCurrentUser,
  setBackendRolePermissions,
} from '~/src/store/slices/root-slice'
import {useAppDispatch} from '~/src/store/store-hooks'
import {isNullOrUndefined} from '~/src/utils'
import {getRouteForRole, type ModulePermission} from '~/src/utils/routeUtils'
import {inferCategoryFromPermissions} from '~/src/utils/permissionUtils'
import ExpiredPasswordDialog from './ExpiredPasswordDialog'

const FormTitle = styled('div')(
  () => css`
    font-size: 24px;
    font-weight: 400;
    margin-top: 50px;
  `,
)

const FieldContainer = styled('div')(
  ({theme}) => css`
    display: flex;
    flex-direction: column;
    row-gap: ${theme.spacing(4)};
    margin-top: ${theme.spacing(5)};
  `,
)

const ForgotPWLinkContainer = styled('div')(
  () => css`
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
)

const ForgotPWLink = styled(NavLink)(
  ({theme}) => css`
    color: ${theme.palette.text.primary};
    font-size: 11px;
    line-height: 13px;
    text-align: right;
    text-decoration: none;
  `,
)

interface LocationState {
  email?: string
  password?: string
}

export default function SigninPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{emailAddress?: string; token?: string}>()
  const locationState = location.state as LocationState | undefined
  const {search} = useLocation()
  const {showError, showInfo} = useGlobalSnackbar()

  // SHR-FE logic: Check for tempEmail and token in URL params
  const tempEmail = params?.emailAddress?.length ? params.emailAddress : undefined
  const token = params?.token?.length ? params.token : undefined

  const tempPassword = locationState?.password
  const [getCurrentUser] = useLazyGetCurrentUserQuery()
  const [signin, {error: signinError}] = useSigninMutation()
  const [getGaAgencyNamesAndIds] = useLazyGetGaAgencyNamesAndIdsQuery()
  const [isLoading, setIsLoading] = useState(false)
  const paramValues = queryString.parse(search)
  const [expiredPasswordDialog, setExpiredPasswordDialog] = useState(false)

  const fetchUserDetails = useCallback(
    async function (props: SigninResponse | FirstSigninResponse | MSPAuthResponse) {
      // Handle legacy first signin flow
      if ('email_verified' in props) {
        setIsLoading(false)
        navigate(BASE_ROUTES.PASSWORD_RESET, {
          state: {token: props.oldPassword, email: props.email, params: paramValues},
        })
        return undefined
      }
      // Handle MSP authentication response
      else if ('accessToken' in props) {
        // Extract user info from JWT token to validate we have a valid token
        const userInfo = extractUserFromToken(props.idToken)
        if (!userInfo) {
          throw new Error('Failed to extract user information from token')
        }

        // Fetch full user details from API using /user/me endpoint
        // This endpoint uses the JWT to identify the user, bypassing authorization
        const user = await getCurrentUser().unwrap()
        dispatch(updateCurrentUser(user))
        return user
      }
      // Handle legacy signin response
      else {
        const user = await getCurrentUser().unwrap()
        dispatch(updateCurrentUser(user))
        return user
      }
    },
    [dispatch, getCurrentUser, navigate, paramValues],
  )

  const fetchAndStorePermissions = useCallback(
    async function (
      user?: IUserBase,
    ): Promise<
      {user: IUserBase; category: string; modulePermissions?: ModulePermission[]} | undefined
    > {
      if (isNullOrUndefined(user)) {
        return
      }

      // If multiple roles, redirect to role selection page
      if (user.roles.length > 1) {
        navigate(BASE_ROUTES.ROLE_SELECT)
        return
      }

      try {
        // Fetch merged permissions for all user roles using the new API
        const permissionsResponse = await roleApi.getPermissionsForRoles(user.roles as string[])

        let category = 'Platform' // Default category
        let modulePermissions: ModulePermission[] | undefined

        // Map the API response to BackendRolePermissions format and store in Redux
        if (permissionsResponse.roles.length > 0) {
          const primaryRole = permissionsResponse.roles[0]
          if (primaryRole) {
            // Use role name for routing (category means "where role is defined", not "where to route")
            category = primaryRole.name

            // Permission priority order:
            // 1. User's custom modulePermissions (user-specific overrides)
            // 2. Agency assignment permissions (for multi-agency users)
            // 3. Role default permissions (fallback)
            const userCustomPermissions = user.modulePermissions as ModulePermission[] | undefined
            const hasUserPermissions = userCustomPermissions && userCustomPermissions.length > 0

            const firstAgencyPermissions = user.agencyAssignments?.[0]?.modulePermissions
            const hasAgencyPermissions = firstAgencyPermissions && firstAgencyPermissions.length > 0

            // Determine which permissions to use based on priority
            if (hasUserPermissions) {
              // User has custom permissions - use those (highest priority)
              modulePermissions = userCustomPermissions
            } else if (hasAgencyPermissions) {
              // User has agency-specific permissions
              modulePermissions = firstAgencyPermissions as ModulePermission[]
            } else {
              // Fall back to role default permissions
              modulePermissions = primaryRole.modulePermissions as ModulePermission[]
            }

            dispatch(
              setBackendRolePermissions({
                _id: primaryRole.id,
                id: primaryRole.id,
                name: primaryRole.name,
                accessList: primaryRole.accessList,
                // Store the effective modulePermissions based on priority
                modulePermissions: modulePermissions,
                isPlatform: primaryRole.category === 'Platform',
                // Store category for layout variant determination (custom roles need this)
                category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
                application: 'msp',
                createdDate: primaryRole.createdDate?.toString() ?? '',
                modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
                versionKey: Number(primaryRole.versionKey) || 0,
              }),
            )

            // Store role in localStorage for compatibility
            window.localStorage.setItem('cognitoRole', JSON.stringify({role: primaryRole}))
          }
        } else {
          // Role not found in role definitions - this happens for custom roles
          // Fall back to using the user's modulePermissions directly from /user/me
          const userCustomPermissions = user.modulePermissions as ModulePermission[] | undefined
          const hasUserPermissions = userCustomPermissions && userCustomPermissions.length > 0

          const firstAgencyPermissions = user.agencyAssignments?.[0]?.modulePermissions
          const hasAgencyPermissions = firstAgencyPermissions && firstAgencyPermissions.length > 0

          // Determine which permissions to use based on priority
          if (hasUserPermissions) {
            modulePermissions = userCustomPermissions
          } else if (hasAgencyPermissions) {
            modulePermissions = firstAgencyPermissions as ModulePermission[]
          }

          if (modulePermissions && modulePermissions.length > 0) {
            // Infer category from user's permissions
            category = inferCategoryFromPermissions(modulePermissions)

            // Store permissions in Redux for custom roles
            dispatch(
              setBackendRolePermissions({
                _id: user.roles[0] ?? 'custom',
                id: user.roles[0] ?? 'custom',
                name: user.roles[0] ?? 'Custom Role',
                accessList: [], // Custom roles may not have accessList
                modulePermissions: modulePermissions,
                // Infer isPlatform from category
                isPlatform: category === 'Platform',
                category: category as 'Platform' | 'MSP' | 'Agency' | 'Company',
                application: 'msp',
                createdDate: '',
                modifiedDate: '',
                versionKey: 0,
              }),
            )

            // Store role in localStorage for compatibility
            window.localStorage.setItem(
              'cognitoRole',
              JSON.stringify({
                role: {
                  id: user.roles[0],
                  name: user.roles[0],
                  category: category,
                },
              }),
            )
          }
        }

        return {user, category, modulePermissions}
      } catch (error) {
        showError(error)
        throw error
      }
    },
    [dispatch, navigate, showError],
  )

  const goToDashboard = useCallback(
    async function (result?: {
      user: IUserBase
      category: string
      modulePermissions?: ModulePermission[]
    }) {
      if (isNullOrUndefined(result)) return

      const {user, category, modulePermissions} = result

      // Set session login time for 10-hour timeout tracking
      setSessionLoginTime()

      dispatch(updateUserSignIn({data: true}))
      dispatch(updateLoggedInCompanyId(user.companyId?.length > 0 ? user.companyId : ''))

      // Determine the logged-in agency based on user type:
      // 1. Multi-agency users (agencyAssignments): auto-select the first agency
      // 2. General admin users: fetch their agency list from member access
      // 3. Single agency users: use their assigned agencyId
      const hasMultiAgencyAssignments = user.agencyAssignments && user.agencyAssignments.length > 0
      const isGeneralAdmin = user.roles?.includes('general_admin')

      if (hasMultiAgencyAssignments) {
        // Multi-agency user: auto-select the first agency assignment
        // User can switch later via the SelectAgencyMenu
        const firstAgency = user.agencyAssignments![0]
        dispatch(updateLoggedInAgencyId(firstAgency.agencyId))
      } else if (isGeneralAdmin) {
        try {
          // This will populate agencyNamesAndIds in Redux store (via agency-slice matcher)
          const agencies = await getGaAgencyNamesAndIds(user.id).unwrap()
          // Set the first agency as the logged-in agency so they have a default context
          if (agencies.length > 0) {
            dispatch(updateLoggedInAgencyId(agencies[0].id))
          } else {
            dispatch(updateLoggedInAgencyId(''))
          }
        } catch (error) {
          console.error('Failed to fetch agencies for general_admin:', error)
          dispatch(updateLoggedInAgencyId(''))
        }
      } else {
        // For other roles, use their assigned agency
        dispatch(updateLoggedInAgencyId(user.agencyId?.length > 0 ? user.agencyId : ''))
      }

      // Navigate based on role name and permissions
      const targetRoute = getRouteForRole(category, modulePermissions) // category now holds role name
      navigate(targetRoute)
    },
    [dispatch, navigate, getGaAgencyNamesAndIds],
  )

  const handleSubmit = useCallback(
    async function (emailAddress: string, password: string) {
      try {
        setIsLoading(true)
        await signin({emailAddress: emailAddress, password: password})
          .unwrap()
          .then(fetchUserDetails)
          .then(fetchAndStorePermissions)
          .then(goToDashboard)
      } catch (error: unknown) {
        setIsLoading(false)
        showError(error)
      }
    },
    [signin, fetchUserDetails, fetchAndStorePermissions, goToDashboard, showError],
  )

  const handleTokenAuth = useCallback(
    async function (passedInToken: string) {
      try {
        setIsLoading(true)

        // Validate token first
        if (isTokenExpired(passedInToken)) {
          throw new Error('Token has expired')
        }

        // Create auth response from token
        const authResponse = createAuthResponseFromToken(passedInToken)
        if (!authResponse) {
          throw new Error('Invalid token format')
        }

        // Process the token-based authentication
        await Promise.resolve(authResponse)
          .then(fetchUserDetails)
          .then(fetchAndStorePermissions)
          .then(goToDashboard)
      } catch (error: unknown) {
        setIsLoading(false)
        showError(error)
      }
    },
    [fetchUserDetails, fetchAndStorePermissions, goToDashboard, showError],
  )

  // SHR-FE redirect logic: If we have tempEmail and token, redirect to PASSWORD_RESET
  useEffect(() => {
    if (tempEmail && token) {
      navigate(BASE_ROUTES.PASSWORD_RESET, {
        state: {
          email: tempEmail,
          token: token,
        },
      })
    }
  }, [tempEmail, token, dispatch, navigate])

  // Check for session expired message (from auto-logout)
  useEffect(() => {
    const sessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY)
    if (sessionExpired) {
      // Show the message and clear the flag
      showInfo('You were signed out because your session expired.')
      sessionStorage.removeItem(SESSION_EXPIRED_KEY)
    }
  }, [showInfo])

  useEffect(() => {
    if (!isLoading) {
      // Auto-login from password confirmation page
      const autoLoginEmail = locationState?.email ?? tempEmail
      const autoLoginPassword = tempPassword

      if (autoLoginEmail && autoLoginPassword) {
        void handleSubmit(autoLoginEmail, autoLoginPassword)
      } else if (Object.keys(paramValues).length > 0) {
        // Check if we have a token parameter (from email redirect)
        if (paramValues.token) {
          void handleTokenAuth(paramValues.token as string)
        }
        // Check if we have both email and password parameters
        else if (paramValues.emailAddress && paramValues.password) {
          void handleSubmit(paramValues.emailAddress as string, paramValues.password as string)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, handleTokenAuth, locationState, tempEmail, tempPassword])

  return (
    <LoadingBoundary isLoading={isLoading}>
      <SignInContainer customHeader="Welcome Back">
        <ExpiredPasswordDialog
          isModalOpen={expiredPasswordDialog}
          onModalClose={() => {
            setExpiredPasswordDialog(false)
          }}
        />
        <FormTitle>Sign In</FormTitle>
        <Formik
          validateOnChange={true}
          initialValues={{
            email: '',
            password: '',
            rememberMe: false,
          }}
          validationSchema={Yup.object({
            password: Yup.string().required(errorText.passwordRequired),
            email: Yup.string().email(errorText.emailInvalid).required(errorText.emailRequired),
          })}
          onSubmit={async function (values: {email: string; password: string}) {
            void handleSubmit(values.email, values.password)
          }}>
          {({isSubmitting}) => (
            <Form>
              <FieldContainer>
                <ErrorBox>
                  {(signinError as {data?: {message?: string}})?.data?.message ??
                    (signinError as {message?: string})?.message}
                </ErrorBox>
                <FormikTextField
                  InputLabelProps={{
                    shrink: true,
                  }}
                  name="email"
                  label="Email"
                  type="email"
                />
                <PasswordFormikTextField
                  InputLabelProps={{
                    shrink: true,
                  }}
                  name="password"
                  label="Password"
                  type="password"
                />
              </FieldContainer>
              <ForgotPWLinkContainer>
                <Checkbox name="rememberMe" label="Remember Me" disabled={isSubmitting} />
                <ForgotPWLink to={BASE_ROUTES.FORGOT_PASSWORD}>Forgot Password?</ForgotPWLink>
              </ForgotPWLinkContainer>
              <Button
                fullWidth
                sx={{mt: 5}}
                disabled={isSubmitting}
                variant="contained"
                type="submit">
                Sign In
              </Button>
            </Form>
          )}
        </Formik>
      </SignInContainer>
    </LoadingBoundary>
  )
}
